import { useCallback, useState, useEffect } from "react";
import { Grid, Typography, Button, Paper, Link, Theme, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  IReportTreeNode,
  IReportFile,
  IReportWebdatarocks,
  IUser,
  ReportType,
  ReportUrlType,
} from "@vmosolutions/oc-interfaces";

import ReactMarkdown from "react-markdown";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector, useDispatch } from "react-redux";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { convertDateObjectToString, isDevEnv } from "src/utils/index";

// Custom Field import
import { CustomReportFields } from "src/components/Custom/CustomReportFields";

// API calls
import { Auth } from "aws-amplify";
import { openSnackbar } from "src/redux/actions/App.Actions";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper"; //INotifications
import { ShowUserInfo } from "src/components/ShowUserInfo/ShowUserInfo";
import _ from "lodash";
import { useTheme } from "@mui/material/styles";
import moment from "moment";

interface IReportDetailsProps {
  id: string;
  path: string[];
  activeNode: IReportTreeNode;
  setActiveNode: (node: IReportTreeNode) => void;
}

/**
 * Function that will convert object to an URL param list.
 * @param obj - object you wish to serialize
 * @param path - option path result will be appended to before return.
 */
const object2URLParam = (obj: any, path?: string): string => {
  let params = "";
  for (const key in obj) {
    if (params !== "" && obj[key]) {
      params += "&";
    }
    if (obj[key]) {
      params += key + "=" + encodeURIComponent(obj[key]);
    }
  }
  return path ? path + "?" + params : params;
};

export interface IFullReport extends Omit<IReportFile, "nodeType">, Omit<IReportWebdatarocks, "nodeType"> {
  nodeType: ReportType.file | ReportType.webdatarocks | ReportType.folder | ReportType.email;
}

/**
 * Function which generates the report's url is urlType is not "absolute"
 * @param domain - Report Domain path supplied by the Report.
 * @param path - Report endpoint path.
 */
const returnFullURL = (urlDomain: ReportUrlType, path: string): string => {
  const domain = `https://${urlDomain}`;
  const apiPATH = `${domain}${path}`;
  return apiPATH;
};

/**
 * Get and Display Report or file info based on a supplied fullReport._id
 * @param props -
 */
export const ReportDetails = (props: IReportDetailsProps): JSX.Element => {
  const { id, path, activeNode } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  // Available domains to use for report or file urls.
  const domains = useSelector((state: IRootState) => state.structures.customerStructure.globalAppConfig.domains);
  const reportLink = useSelector((state: IRootState) => state.snackbar.actionURL);

  const [fullReport, setFullReport] = useState<IFullReport>(null);
  const [downloadSection, setDownloadSection] = useState<JSX.Element>(null);
  const [reportGenerationTime, setReportGenerationTime] = useState<Date>(null);
  const [reportType, setReportType] = useState(null);
  const [prevLink, setPrevLink] = useState(null);

  useEffect(() => {
    const reportNodeId = activeNode?.nodeType !== "folder" ? activeNode?._id : null;
    if (!activeNode || !reportNodeId) {
      setFullReport(null);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }
    let ignore = false;
    AmplifyAPIWrapper.Get<IFullReport>(`/reports/${reportNodeId}`).then((newFullReport) => {
      if (newFullReport?._id && !ignore) {
        const { urlType, nodeType, data } = newFullReport;
        const urlTypeKey = urlType as unknown as keyof typeof domains;
        const urlDomain = urlType && domains[urlTypeKey] ? domains[urlTypeKey] : undefined;
        // Modify Report path url and append domain if not "absolute".
        const isReport = nodeType === ReportType.webdatarocks;
        if (urlDomain && isReport && urlType !== ReportUrlType.absolute) {
          if (data?.dataSource?.url) {
            data.dataSource.url = returnFullURL(urlDomain as ReportUrlType, data.dataSource.url);
          }
          if (data?.dataSource?.filename) {
            data.dataSource.filename = returnFullURL(urlDomain as ReportUrlType, data.dataSource.filename);
          }
        } else if (
          urlDomain &&
          (newFullReport?.nodeType === ReportType.file || newFullReport?.nodeType === ReportType.email) &&
          urlType !== ReportUrlType.absolute
        ) {
          newFullReport.url = returnFullURL(urlDomain as ReportUrlType, newFullReport.url);
        }
        setFullReport(newFullReport);
        setDownloadSection(null);
        setPrevLink(null);
      }
    });
    return () => {
      ignore = true;
    };
  }, [domains, activeNode]);

  useEffect(() => {
    const currentLink = reportLink ? reportLink : prevLink ? prevLink : null;
    if (currentLink && fullReport?.name === reportType) {
      setDownloadSection(
        <Grid
          container
          alignItems="center"
          sx={(theme) => ({
            backgroundColor: theme.palette.success.dark,
            width: "fit-content",
            padding: "15px",
            borderRadius: "5px",
          })}
          item
        >
          <CheckCircleIcon sx={{ color: theme.palette.primary.contrastText }} />
          <Typography
            sx={(theme: Theme) => ({ margin: "0px 10px", fontSize: "14px", color: theme.palette.primary.contrastText })}
          >
            "{fullReport?.name}" Report File Ready for
            <Link sx={{ ml: 1, fontWeight: 900, color: "white", textDecoration: "none" }} href={currentLink}>
              DOWNLOAD
            </Link>
            <br />
            <Box sx={{ fontSize: "0.6rem" }}>
              (Generated: {moment(reportGenerationTime).format("MMM-DD-YYYY hh:mm")})
            </Box>
          </Typography>
        </Grid>
      );
    } else {
      setDownloadSection(null);
    }
    setPrevLink(reportLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullReport, reportLink, reportType]);

  const openReportWindow = useCallback(
    (customFieldValues: any): void => {
      // If report required parameters, generate the url and pass it to window.
      const URLparams = customFieldValues ? object2URLParam(customFieldValues) : undefined;
      const webdatarocksReport = { ...fullReport };
      dispatch(openWindow(fullReport?.name, "ReportingWindow", path, { webdatarocksReport, URLparams }, "content"));
    },
    [fullReport, dispatch, path]
  );

  const generateFile = useCallback(
    async (customFieldValues: any): Promise<void> => {
      // If file requires parameters, return them here from supplied customFieldValues.
      const URLparams = customFieldValues
        ? `${fullReport?.url.indexOf("?") > -1 ? "&" : "?"}${object2URLParam(customFieldValues)}`
        : undefined;
      const apiPATH = `${fullReport?.url}${URLparams}`;

      try {
        setReportType(fullReport.name);
        const userSession = await Auth.currentSession();
        const token = userSession.getIdToken().getJwtToken();
        const response = await fetch(apiPATH, {
          method: "POST",
          headers: {
            Accept: "*/*",
            Authorization: token,
          },
          body: null,
        });

        if (response?.status === 202) {
          setReportGenerationTime(new Date());
          // FILE BEING GENERATED
          dispatch(
            openSnackbar({
              open: true,
              closeDelay: 6000,
              variant: "info",
              message: `"${fullReport.name}" file being generated, ${
                fullReport.nodeType === ReportType.email
                  ? "report will be sent via email."
                  : "download link will popup here when ready."
              } `,
            })
          );
        } else {
          // ERROR With the api call.
          dispatch(
            openSnackbar({
              open: true,
              closeDelay: null,
              variant: "error",
              message:
                "ERROR: There was a problem attempting to generate your file, please see an administrator for assistance.",
            })
          );
        }
      } catch (error) {
        dispatch(
          openSnackbar({
            open: true,
            closeDelay: null,
            variant: "error",
            message:
              "ERROR: There was a problem attempting to generate your file, please see an administrator for assistance.",
          })
        );
        // return error;
      }
    },
    [fullReport, dispatch]
  );

  if (!fullReport) {
    return (
      <Grid item>
        <Typography>Select report or file to get more info.</Typography>
      </Grid>
    );
  }

  if (fullReport?.nodeType === ReportType.folder && fullReport?.description) {
    return (
      <Grid container direction="column" className="report_details" wrap="nowrap">
        <Grid container wrap="wrap">
          <Typography className="label" variant="body2">
            Folder Name
          </Typography>
          <Typography className="value" color="textPrimary" variant="body2">
            {fullReport.name}
          </Typography>
        </Grid>
        <Grid item style={{ display: "flex", flexGrow: 1, flexBasis: 0, flexDirection: "column", overflow: "auto" }}>
          <Paper sx={{ px: 2 }}>
            <ReactMarkdown>{fullReport.description ?? ""}</ReactMarkdown>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  const isReport = fullReport?.nodeType === ReportType.webdatarocks;
  const customSchema = fullReport?.jsonSchemaFields;

  return (
    <Grid container direction="column" className="report_details" wrap="nowrap">
      <Grid container wrap="wrap">
        <Typography className="label" variant="body2">
          {isReport ? "Report Name:" : "File Name:"}
        </Typography>
        <Typography className="value" color="textPrimary" variant="body2" sx={{ marginLeft: "84px !important" }}>
          {fullReport.name}
        </Typography>
      </Grid>
      {isDevEnv() && (
        <>
          <Grid container item wrap="wrap">
            <Typography className="label" variant="body2">
              Created By:
            </Typography>
            <Typography className="value" color="textPrimary" variant="body2">
              <ShowUserInfo
                userId={fullReport?.createdBy}
                getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`}
              />
              {" - "}
              {convertDateObjectToString(new Date(fullReport.createdAt))}
            </Typography>
          </Grid>

          <Grid container item wrap="wrap">
            <Typography className="label" variant="body2">
              Last Modified By:
            </Typography>
            <Typography className="value" color="textPrimary" variant="body2" sx={{ marginLeft: "42px !important" }}>
              <ShowUserInfo
                userId={fullReport?.modifiedBy}
                getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`}
              />
              {" - "}
              {convertDateObjectToString(new Date(fullReport.modifiedAt))}
            </Typography>
          </Grid>
        </>
      )}

      <Grid container wrap="wrap">
        {fullReport.readonly && (
          <Typography color="error" variant="body2">
            File is readonly
          </Typography>
        )}
      </Grid>

      <Grid item style={{ marginTop: 15 }} key={`report_${fullReport?._id}`}>
        {customSchema ? (
          <CustomReportFields
            windowId={id}
            customSchema={customSchema}
            onSubmit={_.debounce((customFieldValues: any) => {
              setDownloadSection(null);
              if (isReport) {
                openReportWindow(customFieldValues);
              } else {
                generateFile(customFieldValues);
              }
            }, 500)}
            isReport={isReport}
          />
        ) : isReport ? (
          <>
            <Button
              onClick={_.debounce(openReportWindow, 500)}
              className="openreport_btn"
              variant="contained"
              color="primary"
            >
              Open Report
            </Button>
          </>
        ) : (
          <Button
            onClick={_.debounce(generateFile, 500)}
            className="openreport_btn"
            variant="contained"
            color="primary"
          >
            Generate File
          </Button>
        )}
      </Grid>

      {downloadSection ?? <></>}

      <Grid item style={{ display: "flex", flexGrow: 1, flexBasis: 0, flexDirection: "column", overflow: "auto" }}>
        <Paper sx={{ px: 2 }}>
          <ReactMarkdown>{fullReport.description ?? ""}</ReactMarkdown>
        </Paper>
      </Grid>
    </Grid>
  );
};
